import { type ReactNode } from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { DateField, Show, useRecordContext } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { NotAuthorized, RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess, type AccessibleRegion } from '../auth/permissions';
import type { Market } from '../dataProvider';
import { TransitionButton } from '../actions/TransitionButton';
import { formatMoney } from './formatMoney';
import { MemberCell, StatusChip } from './CashbackList';

/**
 * Cashback detail (Show) — the MANUAL-INTERVENTION tool for stuck / disputed /
 * fraudulent transactions (normal confirmation is driven by the affiliate feed,
 * not a human). The admin can Confirm or Reject an in-flight cashback
 * (D-cashback-reject).
 *
 * Legal transitions: only a `pending` or `waiting` transaction can be confirmed
 * (`confirmed`) or rejected (`rejected`). A `confirmed`/`rejected`/`cancelled`
 * one shows NO actions. (The model keeps `cancelled`, but there is no dedicated
 * admin action for it now — a reason-coded stop is deferred.)
 *
 * Each action is a single status write through the provider — audited for free by
 * the #25 wrapper, no audit code here. Acting needs `cashbacks:resolve`; a viewer
 * sees no buttons and the transition is rejected at the chokepoint if attempted.
 */

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

const RESOLVABLE = new Set(['pending', 'waiting']);

const CashbackShowLayout = () => {
  const record = useRecordContext();
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  if (!record) return null;

  const inScope = accessibleRegions.includes(
    (record.market as Market).toUpperCase() as AccessibleRegion,
  );
  if (!inScope) return <NotAuthorized />;

  const status = record.status as string;
  const canResolve = hasAccess(
    { role, accessibleRegions },
    { capability: 'cashbacks:resolve' },
  );
  const showActions = RESOLVABLE.has(status) && canResolve;

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Card>
        <CardContent>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            <MemberCell />
            <StatusChip />
          </Stack>

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
            }}
          >
            <Field label="Store">{(record.storeName as string) ?? ''}</Field>
            <Field label="Market">
              {(record.market as string).toUpperCase()}
            </Field>
            <Field label="Order value">
              {formatMoney(record.grossAmount, record.currency)}
            </Field>
            <Field label="Cashback">
              {formatMoney(record.cashbackAmount, record.currency)}
            </Field>
            <Field label="Date">
              <DateField source="occurredAt" showTime />
            </Field>
          </Box>

          {showActions && (
            <>
              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                <TransitionButton
                  resource="transactions"
                  capability="cashbacks:resolve"
                  id={record.id}
                  fromStatus={status}
                  toStatus="confirmed"
                  label="Confirm"
                  color="success"
                  confirmTitle="Confirm this cashback?"
                  confirmContent="Confirm this cashback? It will count toward the member's balance."
                  successMessage="Cashback confirmed"
                />
                <TransitionButton
                  resource="transactions"
                  capability="cashbacks:resolve"
                  id={record.id}
                  fromStatus={status}
                  toStatus="rejected"
                  label="Reject"
                  color="error"
                  confirmTitle="Reject this cashback?"
                  confirmContent="Reject this cashback? It will not be paid to the member."
                  successMessage="Cashback rejected"
                />
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

/** The detail view. Exported for testing without the access wrapper. */
export const CashbackShowView = () => (
  <Show title="Cashback" actions={false} resource="cashbacks">
    <CashbackShowLayout />
  </Show>
);

/** Gated entry point registered as the `cashbacks` Resource's show. */
export const CashbackShow = () => (
  <RequireAccess capability="cashbacks:read">
    <CashbackShowView />
  </RequireAccess>
);
